// @flow
import type {
  Change,
  ImpressionStats,
  MutationObserver,
  MutationObserverOptions,
} from 'app/packages/impression-logging/types';

const NANOSECOND_MULTIPLIER = 1000000;
const DEBUG =
  !__SERVER__ &&
  (window.location.search.includes('debug_impressions=1') ||
    document.cookie.indexOf('debug_impressions=1') > -1);

const Debug = {
  paused: 'Pause',
  resumed: 'Resume',
  stopped: 'Flush',
  enter: 'Enter viewport',
  exit: 'Exit viewport',
};
const debugLoggedIds = {};
const logged = { background: '#FF8A8A', transform: 'scale(.85)' };
const active = { background: '#A0DCC8', transform: 'scale(.99)' };
const resumed = { background: '#0FA573', transform: 'scale(.99)' };
const Styles = {
  init: (id) => ({
    transform: `scale(${debugLoggedIds[id] ? 0.8 : 0.99})`,
    transition: 'transform .2s ease-in-out',
    background: '#8E8E8E',
  }),
  [Debug.enter]: active,
  [Debug.exit]: logged,
  [Debug.stopped]: logged,
  [Debug.paused]: logged,
  [Debug.resumed]: resumed,
};

export default class ImpressionObserver {
  constructor(node: HTMLElement) {
    this.enterCallbacks = [];
    this.exitCallbacks = [];
    this.inViewport = false;
    this.node = node;
    this.startTime = 0;
    this.debugId = '';
  }

  onEnterViewport(callback: () => void) {
    this.enterCallbacks.push(callback);
    return this;
  }

  onExitViewport(callback: (ImpressionStats) => void) {
    this.exitCallbacks.push(callback);
    return this;
  }

  setMutationObserver = (newObserver: MutationObserver) => {
    this.mutationObserver = newObserver;
    return this;
  };

  startMutationObserver = (options: MutationObserverOptions) => {
    if (this.mutationObserver) {
      this.mutationObserver.observe(this.node, options);
    }
  };

  stopMutationObserver = () => {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  };

  setDebugId(id: string) {
    this.debugId = id;
    if (DEBUG) {
      Object.assign(this.node.style, Styles.init(id));
    }
    return this;
  }

  pause() {
    if (this.inViewport) {
      this._debug(Debug.paused, true);
      this.exitCallbacks.forEach((cb) => cb(this.toJSON()));
    }
    return this;
  }

  resume() {
    if (this.inViewport) {
      const startTime = Date.now();
      this._debug(Debug.resumed, { startTime });
      this.startTime = startTime;
    }
    return this;
  }

  stop(reason: string) {
    if (this.inViewport) {
      this._debug(Debug.stopped, true);
      this.exitCallbacks.forEach((cb) => cb(this.toJSON(reason)));
    }
    return this;
  }

  toJSON(reason?: string = ''): ImpressionStats {
    return {
      startTime: this.startTime * NANOSECOND_MULTIPLIER,
      endTime: Date.now() * NANOSECOND_MULTIPLIER,
      forcedExit: reason,
    };
  }

  toDebugJSON() {
    return {
      pinID: this.debugId,
      startTime: this.startTime,
      endTime: Date.now(),
      duration: `${(Date.now() - this.startTime) / 1000} seconds`,
    };
  }

  _debug(msg: string, debugData?: boolean | Object) {
    if (DEBUG) {
      window.console.log(`📌 ${msg} -- ${this.debugId}`);
      if (debugData === true) {
        window.console.log(this.toDebugJSON());
      }
      if (typeof debugData === 'object') {
        window.console.log(debugData);
      }
      if (Styles[msg]) {
        Object.assign(this.node.style, Styles[msg]);
      }
      switch (msg) {
        // flow error note: (thanikb) .flushed is missing - did you mean .stopped (check declaration of Debug)
        // $FlowFixMe: new errors found from flow 0.71 upgrade
        case Debug.flushed:
        case Debug.paused:
        case Debug.exit:
          debugLoggedIds[this.debugId] = true;
          break;
        default:
          break;
      }
    }
  }
  handleIntersectionChange = (change: Change) => {
    const inViewport = change.intersectionRatio > 0 || change.isIntersecting;
    if (inViewport && !this.inViewport) {
      const startTime = Date.now();
      this.startTime = startTime;
      this._debug(Debug.enter, { startTime, node: this.node });
      this.enterCallbacks.forEach((cb) => cb());
    } else if (!inViewport && this.inViewport) {
      this._debug(Debug.exit, true);
      this.exitCallbacks.forEach((cb) => cb(this.toJSON()));
    }
    this.inViewport = inViewport;
  };
  debugId: string;
  enterCallbacks: Array<() => void>;
  exitCallbacks: Array<(ImpressionStats) => void>;
  inViewport: boolean;
  node: HTMLElement;
  startTime: number;
  mutationObserver: MutationObserver;
}
